import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePhone } from './phone';
import { useApi } from './kit';
import { Mark } from './logo';

// The bar at the top right of the new experience (as Bridge's): Support, and on pages with the chat beside the cards,
// how the screen is split: only the cards, cards and a wide chat (default), or cards and a narrow chat.

export type ChatLayout = 'content' | 'split' | 'narrow';
const KEY = 'olv_chat_layout';
export const readLayout = (): ChatLayout => {
  try { const v = localStorage.getItem(KEY); if (v === 'content' || v === 'split' || v === 'narrow') return v; } catch { /* private mode */ }
  return 'split';
};
export function setLayout(v: ChatLayout) {
  try { localStorage.setItem(KEY, v); } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent('olv-layout', { detail: v }));
}
/** The current layout, kept on <html> as chat-<layout> so the CSS sizes the chat column. */
export function useLayout(active: boolean) {
  const [layout, set] = useState<ChatLayout>(readLayout);
  useEffect(() => {
    const f = (e: Event) => set((e as CustomEvent<ChatLayout>).detail);
    window.addEventListener('olv-layout', f);
    return () => window.removeEventListener('olv-layout', f);
  }, []);
  useEffect(() => {
    const el = document.documentElement;
    el.classList.remove('chat-content', 'chat-split', 'chat-narrow');
    if (active) el.classList.add(`chat-${layout}`);
  }, [layout, active]);
  return layout;
}

const I = (d: string) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
const MODES: Array<[ChatLayout, string, string]> = [
  ['content', 'Only the cards', 'M4 5h16v14H4z'],
  ['split', 'Cards and a wide chat', 'M4 5h16v14H4zM13 5v14'],
  ['narrow', 'Cards and a narrow chat', 'M4 5h16v14H4zM16 5v14'],
];

type BarItem = { key: 'approvals' | 'review' | 'paused' | 'replies' | 'hot' | 'credits' | 'setup'; value: string; text: string; link: string; attention: boolean };
const BAR_ICON: Record<BarItem['key'], string> = {
  approvals: 'M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6zM9 12l2 2 4-4',
  review: 'M4 6h16v12H4zM4 7l8 6 8-6',
  paused: 'M10 4H6v16h4zM18 4h-4v16h4z',
  replies: 'M9 17H5l-2 3V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-6',
  hot: 'M12 2c1 4 5 5 5 10a5 5 0 0 1-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3-1-6 1-9z',
  credits: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM15 9.5c-.5-1-1.6-1.5-3-1.5-1.7 0-3 .8-3 2s1.3 1.7 3 2 3 .8 3 2-1.3 2-3 2c-1.4 0-2.5-.5-3-1.5M12 6v2M12 16v2',
  setup: 'M4 6h16M4 12h10M4 18h6M17 15l2 2 4-4',
};

/** Today in the top bar (what Home's "Outlevo · today" says), as icons with their numbers; what needs you pulses. */
function TodayIcons() {
  const nav = useNavigate();
  const t = useApi<{ bar?: BarItem[] }>('/portal/today', { refetchMs: 60_000 });
  const items = t.data?.bar ?? [];
  if (!items.length) return null;
  return (
    <div className="viewbar-today" role="group" aria-label="Today">
      {items.map((x) => (
        <button key={x.key} type="button" className={`vt-item${x.attention ? ' attention' : ''}${x.value === '0' ? ' zero' : ''}`} title={x.text} aria-label={x.text} onClick={() => nav(x.link)}>
          {I(BAR_ICON[x.key])}
          <span className="vt-val">{x.value}</span>
          {x.attention ? <span className="vt-dot" aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );
}

export function ViewBar({ split }: { split: boolean }) {
  const layout = useLayout(split);
  const phone = usePhone(); // phones: the mark on the left (the menu became the tab bar); Support moved to More
  const support = () => {
    if (split && layout === 'content') setLayout('split');
    window.dispatchEvent(new CustomEvent('olv-support'));
  };
  return (
    <div className="viewbar">
      {phone ? (
        <Link to="/" className="viewbar-brand" aria-label="Outlevo, Today">
          <Mark className="mark" />
        </Link>
      ) : null}
      {split ? (
        <div className="seg viewbar-seg" role="radiogroup" aria-label="Screen layout">
          {MODES.map(([k, label, d]) => (
            <button key={k} type="button" role="radio" aria-checked={layout === k} aria-label={label} title={label} onClick={() => setLayout(k)}>{I(d)}</button>
          ))}
        </div>
      ) : null}
      {/* Today sits at the right, beside Support: the things that need you, where the eye ends */}
      <TodayIcons />
      <button type="button" className="viewbar-support" onClick={support}>
        {I('M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5.6 5.6l3.6 3.6M14.8 14.8l3.6 3.6M18.4 5.6l-3.6 3.6M9.2 14.8l-3.6 3.6')}
        <span>Support</span>
      </button>
    </div>
  );
}
